// API calls for manual reschedule links (PUT api/ams/booking-requests/{id}/reschedule-link).
import { extractApiError } from "@/lib/api-errors"
import { bookingCalendarApi } from "../bookingCalendarApi"

/** Matches RescheduleLinkRequest.Reason on the server. */
export const MIN_LINK_REASON_LENGTH = 3
export const MAX_LINK_REASON_LENGTH = 500

/** The body of the PUT. Each side is named by request id or by reference code, never both. */
export interface RescheduleLinkBody {
  rescheduledFromId?: string
  rescheduledToId?: string
  rescheduledFromReference?: string
  rescheduledToReference?: string
  reason: string
}

export interface RescheduleLinkArgs extends RescheduleLinkBody {
  /** The booking being edited. */
  requestId: string
}

const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/**
 * Builds the request body from what the administrator typed: each side is a booking request id or a
 * reference code (as staff see it). Returns an error message instead when the form cannot be sent;
 * the server still checks everything, and its answer is shown as it comes.
 */
export function buildRescheduleLinkBody(input: {
  requestId: string
  from: string
  to: string
  reason: string
}): { body: RescheduleLinkBody } | { error: string } {
  const from = input.from.trim()
  const to = input.to.trim()
  const reason = input.reason.trim()

  if (!from && !to) return { error: "Enter the booking this one was rescheduled from, or to." }
  if (from && to && from.toLowerCase() === to.toLowerCase()) {
    return { error: "A booking cannot be both the one this was rescheduled from and to." }
  }
  if ([from, to].some(side => side.toLowerCase() === input.requestId.toLowerCase())) {
    return { error: "A booking cannot be rescheduled to or from itself." }
  }
  if (reason.length < MIN_LINK_REASON_LENGTH) return { error: "A reason is required." }

  const body: RescheduleLinkBody = { reason }
  if (from) {
    if (GUID.test(from)) body.rescheduledFromId = from
    else body.rescheduledFromReference = from
  }
  if (to) {
    if (GUID.test(to)) body.rescheduledToId = to
    else body.rescheduledToReference = to
  }
  return { body }
}

/** Turns a refused link into something worth reading — the server says why. */
export function describeRescheduleLinkFailure(error: unknown): string {
  const failure = error as { status?: number | string; data?: unknown }
  const detail = extractApiError(failure?.data)

  if (detail) return detail
  if (failure?.status === 403) return "Only an administrator can link rescheduled bookings."
  if (failure?.status === 404) return "One of the bookings could not be found."
  if (failure?.status === 502) return "The appointment could not be removed from the scheduling system."

  return "The link could not be saved."
}

export const linkApi = bookingCalendarApi.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * Links the booking to the one it was rescheduled from and/or to, replacing any existing link.
     * The "from" booking becomes Rescheduled (and, if still upcoming, is cancelled in Cal), so the
     * grids are invalidated as well as the status views. Every booking whose link changed gets a
     * RescheduleLink note — including ones only named by reference, or unlinked from a previous link —
     * so every booking's notes list is invalidated, not just this one's.
     */
    linkRescheduledBooking: builder.mutation<void, RescheduleLinkArgs>({
      query: ({ requestId, ...body }) => ({
        url: `/api/ams/booking-requests/${requestId}/reschedule-link`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["AmsBookingStatus", "AmsBookings", "AmsBookingNotes"],
    }),
  }),
})

export const { useLinkRescheduledBookingMutation } = linkApi
